import type { MouseEvent, ReactNode } from 'react';

import {
  Button,
  Icon,
  type IconType,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@kognitos/lattice';

/**
 * Props for the TooltipActionButton component.
 */
export interface ITooltipActionButtonProps {
  /** Tooltip text displayed on hover */
  tooltip: string;
  /** Lucide icon name to display in the button */
  icon?: IconType;
  /** Click handler for the button */
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether to show loading spinner */
  isLoading?: boolean;
  /** Button variant style */
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link';
  /** Button size */
  size?: 'icon' | 'default' | 'sm' | 'lg' | 'icon-sm' | 'icon-xs' | 'icon-lg';
  /** Optional children rendered alongside the icon (e.g., text label) */
  children?: ReactNode;
  /** Whether to render as child element (for wrapping Link components) */
  asChild?: boolean;
  /** HTML id attribute for the button */
  id?: string;
  /** Tour target identifier for product tours */
  'data-tour-id'?: string;
  /** Test identifier */
  'data-testid'?: string;
  /** Accessible label for the button */
  'aria-label'?: string;
  /** Additional CSS classes for the button */
  className?: string;
}

/** Default tooltip delay in milliseconds */
const TOOLTIP_DELAY_MS = 200;

/**
 * A button wrapped in a tooltip with a consistent 200ms hover delay.
 *
 * Used across automation page headers for action buttons like Run, Edit,
 * Version History, etc. Provides consistent tooltip behavior per the design system.
 *
 * @param {ITooltipActionButtonProps} props - Component props
 * @returns {JSX.Element} Tooltip-wrapped button component
 *
 * @example
 * <TooltipActionButton
 *   tooltip="Run"
 *   icon="Play"
 *   onClick={handleRun}
 *   variant="default"
 *   size="icon"
 * />
 *
 * @example
 * <TooltipActionButton
 *   tooltip="Run Again"
 *   icon="Play"
 *   onClick={handleRunAgain}
 *   variant="outline"
 *   size="default"
 * >
 *   Run Again
 * </TooltipActionButton>
 */
export function TooltipActionButton({
  tooltip,
  icon,
  onClick,
  disabled = false,
  isLoading = false,
  variant = 'outline',
  size = 'icon',
  children,
  asChild = false,
  className,
  ...rest
}: ITooltipActionButtonProps) {
  return (
    <Tooltip delayDuration={TOOLTIP_DELAY_MS} variant="primary">
      <TooltipTrigger asChild>
        <Button
          variant={variant}
          size={size}
          onClick={onClick}
          disabled={disabled}
          isLoading={isLoading}
          asChild={asChild}
          className={className}
          id={rest.id}
          data-tour-id={rest['data-tour-id']}
          data-testid={rest['data-testid']}
          aria-label={rest['aria-label'] ?? tooltip}
        >
          {icon && !isLoading && <Icon type={icon} size="sm" />}
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
